import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Check, ChevronDown, ChevronUp, Download, FileDown, Loader2, Lock, Paperclip, PenLine,
  Printer, Send, ShieldCheck, Users, MessagesSquare, X,
} from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { MarkdownWithLinks } from '../MarkdownWithLinks';
import { WorksheetFieldInput } from './WorksheetFieldInput';
import { HomeworkAttachment } from '@/components/HomeworkAttachment';
import { hasCompanion } from '../companion/access';

/**
 * The member's worksheet.
 *
 * These run long, so it opens as a card: title, instructions and how far they have got, with the
 * fields folded away until they open it — and already open if they have started.
 *
 * Autosaves as they type **and** has a Submit button, his call. They can download their completed
 * copy, or a blank one to fill in by hand and hand back as a photo or scan.
 */
/** Where a submission goes, matching what the server will do with it. */
const resolveDestination = (sheet, response) => {
  if (sheet?.visibility === 'homework') return response?.destination === 'group' ? 'group' : 'practitioner';
  if (sheet?.visibility === 'private' || sheet?.visibility === 'none') return 'private';
  return response?.destination || 'private';
};


export function WorksheetFill({ worksheet, initialResponse, contentId, onSubmitted }) {
  const [answers, setAnswers] = useState(initialResponse?.answers || {});
  const [status, setStatus] = useState(initialResponse?.status || 'draft');
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [missing, setMissing] = useState([]);
  // Homework-only worksheets hand in to the practitioner (the server enforces it), so the button
  // must say Submit. This was seeded 'private' regardless of the sheet, which made a hand-in read
  // "Save my answers" and looked like the Submit button was missing.
  const [destination, setDestination] = useState(
    resolveDestination(worksheet, initialResponse)
  );
  const [open, setOpen] = useState(
    Object.keys(initialResponse?.answers || {}).length > 0
  );
  const [file, setFile] = useState(
    initialResponse?.file_url
      ? { url: initialResponse.file_url, name: initialResponse.file_name }
      : null
  );
  const [uploading, setUploading] = useState(false);
  const [sheet, setSheet] = useState(worksheet || null);
  const fileInput = useRef(null);

  const timer = useRef(null);
  const dirty = useRef(false);

  // Mounted with just a contentId (older call sites) — fetch it ourselves.
  useEffect(() => {
    if (worksheet || !contentId) return;
    axios.get(`${API}/worksheets/by-content/${contentId}`)
      .then((res) => {
        setSheet(res.data.worksheet);
        if (res.data.response) {
          setAnswers(res.data.response.answers || {});
          setStatus(res.data.response.status || 'draft');
          setDestination(resolveDestination(res.data.worksheet, res.data.response));
          setOpen((prev) => prev || Object.keys(res.data.response.answers || {}).length > 0);
        }
      })
      .catch(() => {});
  }, [worksheet, contentId]);

  const persist = useCallback(async (next) => {
    if (!sheet) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/worksheets/${sheet.id}/response`, { answers: next });
      setSavedAt(res.data.saved_at);
      dirty.current = false;
    } catch (err) { /* it stays dirty and the next keystroke retries */ }
    finally { setSaving(false); }
  }, [sheet]);

  const update = (fieldId, value) => {
    const next = { ...answers, [fieldId]: value };
    setAnswers(next);
    setMissing((prev) => prev.filter((id) => id !== fieldId));
    dirty.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(next), 1200);
  };

  useEffect(() => {
    const warn = (e) => { if (dirty.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      clearTimeout(timer.current);
    };
  }, []);

  const download = async (blank) => {
    try {
      const res = await axios.get(`${API}/worksheets/${sheet.id}/pdf?blank=${blank}`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(sheet.title || 'worksheet').replace(/[^a-z0-9]+/gi, '-')}-${blank ? 'blank' : 'completed'}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('That download did not start — try again'); }
  };

  const print = async () => {
    try {
      const res = await axios.get(`${API}/worksheets/${sheet.id}/pdf?blank=false`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (err) { toast.error('Could not open the print view'); }
  };

  const upload = async (chosen) => {
    if (!chosen) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', chosen);
      const res = await axios.post(`${API}/worksheets/${sheet.id}/upload`, fd);
      setFile({ url: res.data.file_url, name: res.data.file_name });
      toast.success('Attached — press Submit to hand it in');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That file would not upload');
    } finally { setUploading(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      clearTimeout(timer.current);
      const res = await axios.post(`${API}/worksheets/${sheet.id}/submit`, {
        answers, destination,
        file_url: file?.url || null, file_name: file?.name || null,
      });
      setStatus('submitted');
      setMissing([]);
      dirty.current = false;
      toast.success({
        private: 'Saved to your private reflections',
        practitioner: 'Sent to your practitioner',
        group: 'Shared with the group and handed in to your practitioner',
      }[res.data.destination] || 'Submitted');
      if (onSubmitted) onSubmitted(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.missing) {
        setMissing(detail.missing);
        setOpen(true);
        toast.error('A few answers are still needed');
        setTimeout(() => {
          document.querySelector(`[data-testid="worksheet-field-${detail.missing[0]}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 120);
      } else {
        toast.error('That did not save — try again in a moment');
      }
    } finally { setSubmitting(false); }
  };

  if (!sheet) return null;

  const fillable = (sheet.fields || []).filter((f) => !['heading', 'instruction'].includes(f.type));
  const answered = fillable.filter((f) => {
    const v = answers[f.id];
    if (v === null || v === undefined || v === '') return false;
    // A list arrives as ["", "", ""] the moment they touch it, and a fill-in table as rows of
    // those, so count actual written cells rather than trusting the array's length.
    if (Array.isArray(v)) return v.flat(2).some((entry) => String(entry || '').trim() !== '');
    if (typeof v === 'object') return !!v.value;
    return true;
  }).length;
  const choice = sheet.visibility === 'choice';
  const noHandIn = sheet.visibility === 'none';
  // Bronze members have no journal to send a prompt to — Module 1 is open to everyone but the
  // Companion is Silver and above, so the link would lead them nowhere.
  const journalOpen = hasCompanion(JSON.parse(localStorage.getItem('user') || 'null'));

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-7 mb-4"
      data-testid="worksheet-fill">
      {/* Card head — always visible, even when folded away */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-cyan-700 mb-1">Worksheet</p>
          <h3 className="font-serif text-2xl text-gray-900" data-testid="worksheet-title">
            {sheet.title}
          </h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="worksheet-save-state">
          {saving ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
          ) : savedAt || status === 'submitted' ? (
            <><Check className="w-3.5 h-3.5 text-emerald-600" /> Saved</>
          ) : (
            <>Saves as you type</>
          )}
        </div>
      </div>

      {sheet.intro && (
        <div className="mt-4 text-sm text-gray-700" data-testid="worksheet-intro">
          <MarkdownWithLinks>{sheet.intro}</MarkdownWithLinks>
        </div>
      )}

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Button size="sm" variant={open ? 'outline' : 'default'} onClick={() => setOpen(!open)}
          className={open ? '' : 'bg-gradient-to-r from-cyan-500 to-purple-500'}
          data-testid="worksheet-toggle">
          {open
            ? <><ChevronUp className="w-4 h-4 mr-1" /> Fold this away</>
            : <><ChevronDown className="w-4 h-4 mr-1" />
                {answered ? 'Continue worksheet' : 'Open worksheet'}</>}
        </Button>
        <span className="text-xs text-gray-500" data-testid="worksheet-progress">
          {answered} of {fillable.length} answered
          {status === 'submitted' && ' · submitted'}
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <button type="button" onClick={print}
            className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
            data-testid="worksheet-print-btn">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          <button type="button" onClick={() => download(false)}
            className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
            data-testid="worksheet-download-completed">
            <Download className="w-3.5 h-3.5" /> My copy
          </button>
          <button type="button" onClick={() => download(true)}
            className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
            data-testid="worksheet-download-blank">
            <FileDown className="w-3.5 h-3.5" /> Blank copy
          </button>
        </div>
      </div>

      {open && (
        <>
          <div className="space-y-5 mt-7 pt-6 border-t border-gray-100"
            data-testid="worksheet-fields">
            {(sheet.fields || []).map((field) => (
              <div key={field.id}>
                <WorksheetFieldInput
                  field={field}
                  value={answers[field.id]}
                  missing={missing.includes(field.id)}
                  onChange={(v) => update(field.id, v)}
                />
                {field.journal_prompt && journalOpen && (
                  <Link
                    to={`/dashboard/companion/journal?${new URLSearchParams({
                      content_id: contentId || sheet.content_id || '',
                      content_title: sheet.title || '',
                      prompt: field.label || '',
                    }).toString()}`}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-cyan-700
                               hover:text-cyan-900 transition-colors duration-200"
                    data-testid={`worksheet-prompt-to-journal-${field.id}`}
                  >
                    <PenLine className="w-3.5 h-3.5" /> Add this prompt to my journal
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="mt-7 pt-5 border-t border-gray-100 space-y-3">
            {noHandIn ? (
              <p className="text-xs text-gray-500 flex items-center gap-1.5"
                data-testid="worksheet-no-handin-note">
                <Lock className="w-3.5 h-3.5" />
                Nothing to hand in here — this one is yours. Anything you write saves itself as
                you go.
              </p>
            ) : (
              <>
            {choice && (
              <div className="grid gap-2 sm:grid-cols-3" data-testid="worksheet-visibility-choice">
                {[
                  ['private', Lock, 'Keep this private', 'Not handed in — only you ever read it'],
                  ['practitioner', Users, 'Share with my practitioner',
                    'Handed in for this section'],
                  ['group', MessagesSquare, 'Share with the group',
                    'Handed in AND posted anonymously for others'],
                ].map(([key, Icon, heading, note]) => (
                  <button key={key} type="button" onClick={() => setDestination(key)}
                    data-testid={`worksheet-destination-${key}`}
                    className={`flex items-start gap-2 px-4 py-3 rounded-xl border text-sm text-left transition-colors ${
                      destination === key
                        ? 'border-cyan-600 bg-cyan-50 text-gray-900'
                        : 'border-gray-200 text-gray-600 hover:border-cyan-300'}`}>
                    <Icon className="w-4 h-4 shrink-0 mt-0.5" />
                    <span><strong>{heading}</strong><br />
                      <span className="text-xs text-gray-500">{note}</span></span>
                  </button>
                ))}
              </div>
            )}

            {!choice && (
              <p className="text-xs text-gray-500 flex items-center gap-1.5">
                {sheet.visibility === 'homework'
                  ? <><Users className="w-3.5 h-3.5" /> Submitting hands this in to your practitioner.</>
                  : <><Lock className="w-3.5 h-3.5" /> This one is yours alone — it is saved to your
                    private reflections.</>}
              </p>
            )}

            {destination === 'group' && (
              <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-2.5 py-2"
                data-testid="worksheet-group-notice">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                <span>
                  Sharing with the group <strong>also hands this in to your practitioner</strong> for
                  review — you are doing both at once. Other members see the work
                  <strong> anonymously</strong>: never your name or photo. Please don&apos;t write
                  your name in it, or attach anything that shows who you are.
                </span>
              </div>
            )}

            {sheet.allow_upload && (
              <div className="rounded-xl border border-dashed border-gray-300 p-3"
                data-testid="worksheet-upload-block">
                <p className="text-xs text-gray-600 mb-2">
                  Filled in the blank copy by hand? Attach a photo or a scan of it instead.
                </p>
                <input ref={fileInput} type="file" className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.heic,.webp,.doc,.docx,.txt"
                  onChange={(e) => upload(e.target.files?.[0])} />
                {file ? (
                  <div className="flex items-center gap-2 text-sm text-gray-700"
                    data-testid="worksheet-attached-file">
                    <HomeworkAttachment url={file.url} name={file.name} testIdSuffix="-worksheet" />
                    <button type="button" onClick={() => setFile(null)}
                      className="text-gray-400 hover:text-gray-700"
                      data-testid="worksheet-remove-file"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" disabled={uploading}
                    onClick={() => fileInput.current?.click()}
                    data-testid="worksheet-upload-btn">
                    {uploading
                      ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading…</>
                      : <><Paperclip className="w-4 h-4 mr-1" /> Attach my handwritten copy</>}
                  </Button>
                )}
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={submit} disabled={submitting}
                className="bg-gradient-to-r from-cyan-500 to-purple-500"
                data-testid="worksheet-submit-btn">
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  : <><Send className="w-4 h-4 mr-2" />
                      {destination === 'private'
                        ? (status === 'submitted' ? 'Save again' : 'Save my answers')
                        : (status === 'submitted' ? 'Submit again' : 'Submit')}</>}
              </Button>
              {status === 'submitted' && (
                <span className="text-xs text-emerald-700" data-testid="worksheet-submitted-note">
                  {destination === 'private'
                    ? 'Saved. You can keep editing.'
                    : 'Submitted. You can keep editing and submit again.'}
                </span>
              )}
            </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default WorksheetFill;
